<template>
  <div class="container-fluid">

    <div class="page-title">
      <div class="row">
        <div class="col-12">
          <h3>Perfil de empresa</h3>
        </div>
        <div class="col-6">
          <ol class="breadcrumb"></ol>
        </div>
      </div>
    </div>

    <!-- Cargando perfil existente -->
    <div v-if="loadingPerfil" class="text-center py-4">
      <div class="loader-box"><div class="loader-3"></div></div>
      <p class="text-muted mt-2">Cargando datos de tu empresa…</p>
    </div>

    <template v-else>

    <!-- Banner modo edición -->
    <div v-if="empresaId" class="alert alert-info d-flex align-items-center gap-2" role="alert">
      <i class="fa fa-pencil-square-o fa-lg"></i>
      <span>Estás actualizando el perfil de tu empresa. Modifica los campos que necesites y guarda los cambios.</span>
    </div>

    <!-- Alerta de éxito -->
    <div v-if="exito" class="alert alert-success alert-dismissible" role="alert">
      <i class="fa fa-check-circle me-2"></i>
      {{ empresaId ? 'Perfil actualizado correctamente.' : 'Perfil registrado correctamente.' }}
      <button type="button" class="btn-close" @click="exito = false"></button>
    </div>

    <!-- Alerta de error general -->
    <div v-if="errorGeneral" class="alert alert-danger" role="alert">
      <i class="fa fa-exclamation-circle me-2"></i> {{ errorGeneral }}
    </div>

    <form @submit.prevent="guardar" novalidate>

      <!-- ── Identificación ───────────────────────────────────── -->
      <div class="row starter-main">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-building me-2 text-primary"></i>Identificación</h5>
            </div>
            <div class="card-body">
              <div class="row">

                <div class="col-sm-12 col-md-6 mb-3">
                  <label class="form-label required">Nombre / Razón social</label>
                  <input
                    v-model="form.nombre"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errores.nombre }"
                    placeholder="Ej. EcoServicios del Bajío S.A. de C.V."
                  />
                  <div v-if="errores.nombre" class="invalid-feedback">{{ errores.nombre }}</div>
                </div>

                <!-- Logotipo -->
                <div class="col-sm-12 col-md-6 mb-3">
                  <label class="form-label">Logotipo</label>
                  <div class="d-flex align-items-start gap-3">
                    <div
                      v-if="logoPreview || logoActual"
                      class="logo-preview flex-shrink-0"
                    >
                      <img :src="logoPreview || logoActual" alt="Vista previa del logotipo" />
                      <button
                        type="button"
                        class="logo-preview__remove"
                        @click="quitarLogo"
                        title="Quitar imagen"
                      >×</button>
                    </div>
                    <div class="flex-grow-1">
                      <input
                        ref="logoInput"
                        type="file"
                        class="form-control"
                        :class="{ 'is-invalid': errores.logo }"
                        accept=".jpg,.jpeg,.png"
                        @change="handleLogoUpload"
                      />
                      <small class="text-muted">
                        {{ logoActual && !logoPreview ? 'Selecciona una nueva imagen para reemplazar el logo actual' : 'JPG o PNG · máx. 2 MB' }}
                      </small>
                      <div v-if="errores.logo" class="invalid-feedback d-block">{{ errores.logo }}</div>
                    </div>
                  </div>
                </div>

                <div class="col-sm-6 col-md-3 mb-3">
                  <label class="form-label required">Tipo de institución</label>
                  <select
                    v-model="form.tipo_institucion"
                    class="form-select"
                    :class="{ 'is-invalid': errores.tipo_institucion }"
                    :disabled="loadingCatalogos"
                  >
                    <option :value="null" disabled>
                      {{ loadingCatalogos ? 'Cargando…' : 'Selecciona…' }}
                    </option>
                    <option
                      v-for="item in catalogoTiposInstitucion"
                      :key="item.id"
                      :value="item.id"
                    >{{ item.nombre }}</option>
                  </select>
                  <div v-if="errores.tipo_institucion" class="invalid-feedback">{{ errores.tipo_institucion }}</div>
                </div>

                <div class="col-sm-6 col-md-3 mb-3">
                  <label class="form-label required">Tipo de industria</label>
                  <select
                    v-model="form.tipo_industria"
                    class="form-select"
                    :class="{ 'is-invalid': errores.tipo_industria }"
                    :disabled="loadingCatalogos"
                  >
                    <option :value="null" disabled>
                      {{ loadingCatalogos ? 'Cargando…' : 'Selecciona…' }}
                    </option>
                    <option
                      v-for="sector in catalogoSectores"
                      :key="sector.id"
                      :value="sector.id"
                    >{{ sector.nombre }}</option>
                  </select>
                  <div v-if="errores.tipo_industria" class="invalid-feedback">{{ errores.tipo_industria }}</div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Descripción y servicios ──────────────────────────── -->
      <div class="row starter-main">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-file-text me-2 text-primary"></i>Descripción y servicios</h5>
            </div>
            <div class="card-body">

              <div class="mb-4">
                <label class="form-label required">Descripción general</label>
                <RichTextEditor
                  v-model="form.descripcion"
                  placeholder="Describe brevemente a qué se dedica tu empresa…"
                  :invalid="!!errores.descripcion"
                />
                <div v-if="errores.descripcion" class="text-danger small mt-1">{{ errores.descripcion }}</div>
              </div>

              <div class="mb-4">
                <label class="form-label required">Servicios ofrecidos</label>
                <RichTextEditor
                  v-model="form.servicios_ofrecidos"
                  placeholder="Lista los principales servicios o productos que ofrece tu empresa…"
                  :invalid="!!errores.servicios_ofrecidos"
                />
                <div v-if="errores.servicios_ofrecidos" class="text-danger small mt-1">{{ errores.servicios_ofrecidos }}</div>
              </div>

              <div class="mb-4">
                <label class="form-label">Casos de éxito</label>
                <RichTextEditor
                  v-model="form.casos_exito"
                  placeholder="Describe proyectos o logros destacados de tu empresa…"
                />
              </div>

              <div class="mb-1">
                <label class="form-label">Certificaciones</label>
                <RichTextEditor
                  v-model="form.certificaciones"
                  placeholder="Lista las certificaciones, normas o reconocimientos con que cuenta tu empresa…"
                />
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- ── Ubicación ────────────────────────────────────────── -->
      <div class="row starter-main">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-map-marker me-2 text-primary"></i>Ubicación</h5>
            </div>
            <div class="card-body">
              <div class="row">

                <div class="col-sm-6 col-md-4 mb-3">
                  <label class="form-label required">Estado</label>
                  <select
                    v-model="form.estado_id"
                    class="form-select"
                    :class="{ 'is-invalid': errores.estado_id }"
                    :disabled="loadingCatalogos"
                  >
                    <option value="" disabled>
                      {{ loadingCatalogos ? 'Cargando…' : 'Selecciona un estado' }}
                    </option>
                    <option
                      v-for="estado in catalogoEstados"
                      :key="estado.id"
                      :value="estado.id"
                    >{{ estado.nombre }}</option>
                  </select>
                  <div v-if="errores.estado_id" class="invalid-feedback">{{ errores.estado_id }}</div>
                </div>

                <div class="col-sm-6 col-md-4 mb-3">
                  <label class="form-label required">Municipio</label>
                  <input
                    v-model="form.municipio"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errores.municipio }"
                    placeholder="Ej. León"
                  />
                  <div v-if="errores.municipio" class="invalid-feedback">{{ errores.municipio }}</div>
                </div>

                <div class="col-sm-12 col-md-4 mb-3">
                  <label class="form-label">Dirección</label>
                  <input
                    v-model="form.direccion"
                    type="text"
                    class="form-control"
                    placeholder="Calle, número, colonia…"
                  />
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Contacto ─────────────────────────────────────────── -->
      <div class="row starter-main">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0"><i class="fa fa-envelope me-2 text-primary"></i>Contacto</h5>
            </div>
            <div class="card-body">
              <div class="row">

                <div class="col-sm-6 col-md-4 mb-3">
                  <label class="form-label required">Correo electrónico</label>
                  <input
                    v-model="form.contacto_email"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': errores.contacto_email }"
                    placeholder="contacto@empresa.mx"
                  />
                  <div v-if="errores.contacto_email" class="invalid-feedback">{{ errores.contacto_email }}</div>
                </div>

                <div class="col-sm-6 col-md-4 mb-3">
                  <label class="form-label">Teléfono</label>
                  <input
                    v-model="form.telefono"
                    type="tel"
                    class="form-control"
                    placeholder="Ej. 477 123 4567"
                  />
                </div>

                <div class="col-sm-12 col-md-4 mb-3">
                  <label class="form-label">Sitio web</label>
                  <input
                    v-model="form.sitio_web"
                    type="url"
                    class="form-control"
                    :class="{ 'is-invalid': errores.sitio_web }"
                    placeholder="https://www.empresa.mx"
                  />
                  <div v-if="errores.sitio_web" class="invalid-feedback">{{ errores.sitio_web }}</div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Acciones ─────────────────────────────────────────── -->
      <div class="row starter-main mb-4">
        <div class="col-12 d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="limpiar">
            <i class="fa fa-times me-1"></i> Cancelar
          </button>
          <button type="submit" class="btn btn-primary" :disabled="guardando">
            <span v-if="guardando" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <i v-else class="fa fa-save me-1"></i>
            {{ guardando ? 'Guardando…' : 'Guardar perfil' }}
          </button>
        </div>
      </div>

    </form>

    </template><!-- fin v-else loadingPerfil -->

  </div>
</template>

<script>
import api from "@/axios";
import authService from "@/services/authService";
import { jwtDecode } from "jwt-decode";
import RichTextEditor from "@/components/RichTextEditor.vue";

const FORM_VACIO = () => ({
  nombre:              "",
  tipo_institucion:    null,
  tipo_industria:      null,
  descripcion:         "",
  servicios_ofrecidos: "",
  casos_exito:         "",
  certificaciones:     "",
  estado_id:           "",
  municipio:           "",
  direccion:           "",
  contacto_email:      "",
  telefono:            "",
  sitio_web:           "",
});

const CAMPOS_FORM = Object.keys(FORM_VACIO());

// Extrae el id de un campo que puede llegar como número o como { id, nombre }
function resolverIdCatalogo(valor) {
  if (valor == null) return null;
  if (typeof valor === "object") return valor.id ?? null;
  return valor;
}

export default {
  name: "PerfilEmpresa",
  components: { RichTextEditor },

  data() {
    return {
      form:             FORM_VACIO(),
      logoFile:         null,
      logoPreview:      null,
      logoActual:       null,
      empresaId:        null,
      userId:           null,
      errores:          {},
      guardando:        false,
      exito:            false,
      errorGeneral:     null,
      loadingPerfil:    true,
      catalogoEstados:          [],
      catalogoSectores:         [],
      catalogoTiposInstitucion: [],
      loadingCatalogos:         false,
    };
  },

  created() {
    this.resolverUsuario();
    Promise.all([this.fetchCatalogos(), this.fetchEmpresaExistente()]).finally(() => {
      this.loadingPerfil = false;
    });
  },

  methods: {
    resolverUsuario() {
      const userData = authService.getUserData();
      this.userId = userData?.id ?? userData?.pk ?? null;
      if (!this.userId) {
        try {
          const payload = jwtDecode(authService.getAccessToken());
          this.userId = payload?.user_id ?? payload?.id ?? null;
        } catch { /* sin token válido */ }
      }
    },

    async fetchCatalogos() {
      this.loadingCatalogos = true;
      try {
        const { data } = await api.get("catalogos");
        this.catalogoEstados          = data.estados           ?? [];
        this.catalogoSectores         = data.sectores          ?? [];
        this.catalogoTiposInstitucion = data.tipos_institucion ?? [];
      } catch { /* catálogo vacío; formulario sigue usable */ } finally {
        this.loadingCatalogos = false;
      }
    },

    async fetchEmpresaExistente() {
      if (!this.userId) return;
      try {
        const { data } = await api.get(`usuarios/${this.userId}/directorio/`);
        const empresa = data?.directorio?.[0];
        if (empresa?.id) this.poblarFormulario(empresa);
      } catch (err) {
        // 404 = sin registro previo, formulario en modo creación
        if (err.response?.status !== 404) {
          this.errorGeneral = "No se pudo verificar si tienes un perfil previo.";
        }
      }
    },

    poblarFormulario(empresa) {
      this.empresaId = empresa.id;

      // Campos de texto plano
      const directos = [
        "nombre", "descripcion", "servicios_ofrecidos",
        "casos_exito", "certificaciones",
        "municipio", "direccion",
        "contacto_email", "telefono", "sitio_web",
      ];
      directos.forEach((campo) => {
        if (empresa[campo] != null) this.form[campo] = empresa[campo];
      });

      // tipo_institucion y sector llegan como { id, nombre } — solo necesitamos el id
      const tipoInstId = resolverIdCatalogo(empresa.tipo_institucion);
      if (tipoInstId != null) this.form.tipo_institucion = tipoInstId;

      const sectorId = resolverIdCatalogo(empresa.sector ?? empresa.tipo_industria);
      if (sectorId != null) this.form.tipo_industria = sectorId;

      // estado llega como objeto { id, nombre } → solo necesitamos el id
      if (empresa.estado?.id != null) {
        this.form.estado_id = empresa.estado.id;
      }

      // Logo existente
      if (empresa.logo) this.logoActual = empresa.logo;
    },

    validar() {
      const e = {};
      if (!this.form.nombre.trim())
        e.nombre = "Campo obligatorio";
      if (this.form.tipo_institucion == null)
        e.tipo_institucion = "Selecciona un tipo";
      if (this.form.tipo_industria == null)
        e.tipo_industria = "Selecciona una industria";
      if (!this.form.descripcion?.trim() || this.form.descripcion === "<br>")
        e.descripcion = "Campo obligatorio";
      if (!this.form.servicios_ofrecidos?.trim() || this.form.servicios_ofrecidos === "<br>")
        e.servicios_ofrecidos = "Campo obligatorio";
      if (!this.form.estado_id)
        e.estado_id = "Selecciona un estado";
      if (!this.form.municipio.trim())
        e.municipio = "Campo obligatorio";
      if (!this.form.contacto_email.trim())
        e.contacto_email = "Campo obligatorio";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.contacto_email))
        e.contacto_email = "Correo no válido";
      if (this.form.sitio_web && !/^https?:\/\/.+/.test(this.form.sitio_web))
        e.sitio_web = "Debe comenzar con http:// o https://";
      return e;
    },

    handleLogoUpload(event) {
      const file = event.target.files[0];
      this.errores     = { ...this.errores, logo: undefined };
      this.logoFile    = null;
      this.logoPreview = null;
      if (!file) return;

      if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
        this.errores = { ...this.errores, logo: "Solo se permiten archivos JPG o PNG" };
        event.target.value = "";
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        this.errores = { ...this.errores, logo: "El archivo excede el tamaño máximo de 2 MB" };
        event.target.value = "";
        return;
      }
      this.logoFile    = file;
      this.logoPreview = URL.createObjectURL(file);
    },

    quitarLogo() {
      this.logoFile    = null;
      this.logoPreview = null;
      this.logoActual  = null;
      if (this.$refs.logoInput) this.$refs.logoInput.value = "";
    },

    async guardar() {
      this.errores      = this.validar();
      this.errorGeneral = null;
      this.exito        = false;
      if (Object.keys(this.errores).filter((k) => this.errores[k]).length) return;

      // Mapeo de campos del form a nombres que espera el backend
      const CAMPO_PAYLOAD = {
        tipo_institucion: "tipo_institucion_id",
        tipo_industria:   "sector_id",
      };

      const fd = new FormData();
      fd.append("user", this.userId);
      Object.entries(this.form).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        fd.append(CAMPO_PAYLOAD[k] ?? k, v);
      });
      if (this.logoFile) fd.append("logo", this.logoFile);

      this.guardando = true;
      try {
        if (this.empresaId) {
          await api.put(`directorio/${this.empresaId}/`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          const { data } = await api.post("directorio/crear/", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          this.empresaId = data?.id ?? null;
        }
        this.exito = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        const data = err.response?.data;
        if (data && typeof data === "object" && !data.detail) {
          this.errores = Object.fromEntries(
            Object.entries(data).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])
          );
        } else {
          this.errorGeneral = data?.detail ?? "Ocurrió un error al guardar. Intenta de nuevo.";
        }
      } finally {
        this.guardando = false;
      }
    },

    limpiar() {
      this.form         = FORM_VACIO();
      this.errores      = {};
      this.errorGeneral = null;
      this.exito        = false;
      this.quitarLogo();
    },
  },
};
</script>

<style scoped>
.required::after {
  content: " *";
  color: #dc3545;
}

.logo-preview {
  position: relative;
  width: 72px;
  height: 72px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  background: #f8f9fa;
}
.logo-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.logo-preview__remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  padding: 0;
  line-height: 1;
  font-size: .85rem;
  background: rgba(220,53,69,.85);
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.logo-preview__remove:hover {
  background: #dc3545;
}
</style>